import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';

import type { Testimonial } from 'lib/api-routes/ekomi/types';

/**
 * Decodes HTML entities in a string using the browser's DOMParser.
 * This is used to properly display special characters (like emojis 👍) in reviews.
 */
const decodeHtmlEntities = (text: string): string => {
  if (typeof window === 'undefined') {
    // Server-side: return as-is, will be decoded on client
    return text;
  }
  const parser = new DOMParser();
  const doc = parser.parseFromString(text, 'text/html');
  return doc.documentElement.textContent || text;
};

const QuoteIcon = (
  <svg
    width="14"
    height="13"
    viewBox="0 0 14 13"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M0.264589 5.32391V0.385976C0.264405 0.335356 0.274214 0.285193 0.293454 0.238373C0.312695 0.191552 0.340988 0.148999 0.376711 0.11314C0.412433 0.0772809 0.454882 0.0488213 0.501623 0.0294072C0.548364 0.0099932 0.598478 2.21758e-06 0.649089 2.55226e-06H5.14984C5.20045 2.21758e-06 5.25056 0.0099932 5.2973 0.0294072C5.34405 0.0488213 5.38649 0.0772809 5.42222 0.11314C5.45794 0.148999 5.48623 0.191552 5.50547 0.238373C5.52471 0.285193 5.53452 0.335356 5.53434 0.385976V4.50721C5.53434 6.94797 5.25237 8.70489 4.68844 9.77797C3.95765 11.1568 2.8447 12.2169 1.34958 12.9581C1.26351 13.0014 1.16439 13.011 1.07158 12.9852C0.978762 12.9593 0.898916 12.8998 0.847631 12.8182L0.0576584 11.547C0.0278919 11.5007 0.00832765 11.4486 0.000269496 11.3942C-0.00778866 11.3397 -0.00415631 11.2842 0.010925 11.2313C0.0260064 11.1784 0.0521904 11.1293 0.0877327 11.0872C0.123275 11.0452 0.16736 11.0113 0.217051 10.9876C0.980835 10.6119 1.61161 10.0118 2.0249 9.26754C2.463 8.48347 2.72492 7.43231 2.81068 6.11403C2.81392 6.06132 2.80635 6.00849 2.78843 5.95881C2.77051 5.90913 2.74262 5.86363 2.70648 5.82513C2.67034 5.78662 2.6267 5.75591 2.57826 5.73489C2.52982 5.71387 2.47759 5.70298 2.42478 5.70289H0.649089C0.548075 5.7029 0.451114 5.66315 0.379165 5.59224C0.307216 5.52132 0.266058 5.42493 0.264589 5.32391ZM8.72919 5.32391V0.385976C8.72919 0.28361 8.76984 0.185439 8.84221 0.113055C8.91458 0.0406709 9.01274 2.55226e-06 9.11508 2.55226e-06H13.6144C13.6652 -0.000182289 13.7154 0.00967461 13.7623 0.0290061C13.8092 0.0483375 13.8518 0.0767589 13.8877 0.112637C13.9236 0.148514 13.952 0.191136 13.9713 0.238048C13.9907 0.28496 14.0005 0.335238 14.0003 0.385976V4.50721C14.0003 6.94797 13.7179 8.70489 13.153 9.77797C12.4176 11.1568 11.3042 12.2169 9.81278 12.9581C9.7265 13.0014 9.62716 13.0112 9.53411 12.9853C9.44106 12.9595 9.36097 12.8999 9.30943 12.8182L8.52225 11.547C8.49422 11.501 8.4761 11.4496 8.46905 11.3962C8.462 11.3427 8.46617 11.2884 8.48131 11.2367C8.49644 11.185 8.5222 11.137 8.55694 11.0958C8.59168 11.0546 8.63463 11.0211 8.68304 10.9974C9.44657 10.6213 10.0772 10.0213 10.4909 9.27733C10.9281 8.49326 11.1895 7.44209 11.2753 6.12382C11.2787 6.07117 11.2713 6.01838 11.2536 5.9687C11.2358 5.91902 11.2081 5.87351 11.172 5.83498C11.136 5.79645 11.0924 5.76572 11.0441 5.74469C10.9957 5.72365 10.9435 5.71276 10.8908 5.71268H9.11369C9.06289 5.7125 9.01263 5.70228 8.96579 5.68263C8.91895 5.66297 8.87645 5.63426 8.84073 5.59814C8.805 5.56202 8.77676 5.51921 8.75762 5.47215C8.73848 5.42509 8.72882 5.37471 8.72919 5.32391Z"
      fill="#39A949"
    />
  </svg>
);

const Quote = styled(Typography)`
  overflow: hidden;
  font-size: 14px;
`;

const WhiteBox = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  height: '90%',
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  scrollSnapAlign: 'start',
  minWidth: `calc(33.33% - ${theme.spacing(3)})`,
  [theme.breakpoints.down('md')]: {
    minWidth: `calc(50% - ${theme.spacing(2)})`,
  },
  [theme.breakpoints.down('sm')]: {
    minWidth: `calc(80% - ${theme.spacing(1)})`,
  },
}));

const DetailWrapper = styled('div')`
  line-height: 8px;
`;

const StyledLink = styled(Link)`
  text-decoration: none;
`;

export default function Item({ data }: { data: Testimonial }) {
  const dateString = new Date(Number(data.submitted) * 1000).toLocaleString(
    'de-DE',
    {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Europe/Berlin',
    },
  );

  //truncate the reviews to a length of 300 max and adding ...
  const { review } = data;

  const maxCharacters = 300;

  const shortenedReview =
    review.length >= maxCharacters
      ? review.slice(0, maxCharacters) + ' …'
      : review;

  // Review needs to be parsed to display special characters (like emojis 👍) correctly (CAT-1771)
  const parsedShortenedReview = decodeHtmlEntities(shortenedReview);

  return (
    <WhiteBox sx={{ p: 3, m: 1.5 }} aria-roledescription="slide" role="slide">
      <Box
        sx={{
          mb: 4,
        }}
      >
        <Box
          sx={{
            mb: 1,
          }}
        >
          {QuoteIcon}
        </Box>
        <Quote variant="body2">{parsedShortenedReview}</Quote>
      </Box>
      <DetailWrapper>
        <Typography variant="caption">
          {data.rating} von 5 Sterne auf{' '}
          <StyledLink
            href="https://www.ekomi.de/bewertungen-smava-gmbh.html"
            target="_blank"
            rel="noreferrer"
          >
            ekomi.de
          </StyledLink>
          <br />
          <span>vom {dateString.replace(', ', ' um ')} Uhr</span>
        </Typography>
      </DetailWrapper>
    </WhiteBox>
  );
}
